import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AssignmentRule,
  Attribute,
  AttributeWithUsers,
  CreateAttributeRequest,
  CreateSupplierDiscountRequest,
  SupplierDiscount,
} from 'services/bidAnalysis/bidAnalysisServices';
import { AttributeModal } from './components/AttributeModal';
import { RuleModal } from './components/RuleModal';
import { SupplierDiscountModal } from './components/SupplierDiscountModal';
import {
  adminFeeBodyTemplate,
  attributeDescriptionBodyTemplate,
  attributeNameBodyTemplate,
  conditionsBodyTemplate,
  contractTypeBodyTemplate,
  createAttributeActionsBodyTemplate,
  createMappingAttributeActionsBodyTemplate,
  createRuleActionsBodyTemplate,
  createSupplierDiscountActionsBodyTemplate,
  mappingAttributeNameBodyTemplate,
  promptPayDiscountBodyTemplate,
  ruleAttributesBodyTemplate,
  ruleNameBodyTemplate,
  supplierBodyTemplate,
  supplierCodeBodyTemplate,
  supplierNameBodyTemplate,
  userCountBodyTemplate,
  usersListBodyTemplate,
} from './components/tableTemplates';
import { UserAttributeModal } from './components/UserAttributeModal';
import {
  RuleFormState,
  convertFormToPayload,
  convertRuleToForm,
  createEmptyRuleForm,
  validateRuleForm,
} from './ReferenceTables.logic';
import classes from './ReferenceTables.module.scss';

const ReferenceTables = observer(() => {
  const navigate = useNavigate();
  const { BidAnalysisStore } = useStore();
  const [activeTab, setActiveTab] = useState(0);

  // Attribute state
  const [attributeModalVisible, setAttributeModalVisible] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<Attribute | null>(null);
  const [attributeForm, setAttributeForm] = useState<CreateAttributeRequest>({
    attribute_name: '',
    attribute_description: '',
  });
  const [savingAttribute, setSavingAttribute] = useState(false);

  // User-Attribute Mapping state
  const [addUsersModalVisible, setAddUsersModalVisible] = useState(false);
  const [editUsersModalVisible, setEditUsersModalVisible] = useState(false);
  const [selectedAttributeForUsers, setSelectedAttributeForUsers] =
    useState<AttributeWithUsers | null>(null);
  const [selectedAttributeForAdd, setSelectedAttributeForAdd] = useState<Attribute | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [savingUsers, setSavingUsers] = useState(false);

  // Rule state
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<AssignmentRule | null>(null);
  const [savingRule, setSavingRule] = useState(false);
  const [ruleForm, setRuleForm] = useState<RuleFormState>(createEmptyRuleForm());

  // Supplier Discount search state
  const [supplierDiscountSearch, setSupplierDiscountSearch] = useState('');

  // Supplier Discount CRUD state
  const [supplierDiscountModalVisible, setSupplierDiscountModalVisible] = useState(false);
  const [editingSupplierDiscount, setEditingSupplierDiscount] = useState<SupplierDiscount | null>(
    null
  );
  const [supplierDiscountForm, setSupplierDiscountForm] = useState<CreateSupplierDiscountRequest>({
    supplier: '',
    supplier_name: '',
    supplier_code: '',
    prompt_pay_discount: null,
    admin_fee: null,
  });
  const [savingSupplierDiscount, setSavingSupplierDiscount] = useState(false);

  // Load data on mount and tab changes
  useEffect(() => {
    BidAnalysisStore.fetchAttributes();
    BidAnalysisStore.fetchUsersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      BidAnalysisStore.fetchUserMappings();
    } else if (activeTab === 2) {
      BidAnalysisStore.fetchAssignmentRules();
    } else if (activeTab === 3) {
      BidAnalysisStore.fetchSupplierDiscounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Pre-select users when attribute is selected in Add Users modal
  useEffect(() => {
    if (addUsersModalVisible && selectedAttributeForAdd) {
      const attributeWithUsers = BidAnalysisStore.attributesWithUsers.find(
        attr => attr.id === selectedAttributeForAdd.id
      );

      if (attributeWithUsers && attributeWithUsers.users.length > 0) {
        const currentUserIds = attributeWithUsers.users
          .map(attrUser => {
            const matchedUser = BidAnalysisStore.usersList.find(
              (user: any) => user.email?.toLowerCase() === attrUser.user_id?.toLowerCase()
            );
            return matchedUser ? matchedUser.email : attrUser.user_id;
          })
          .filter(Boolean);
        setSelectedUserIds(currentUserIds);
      } else {
        setSelectedUserIds([]);
      }
    }
  }, [
    addUsersModalVisible,
    selectedAttributeForAdd,
    BidAnalysisStore.attributesWithUsers,
    BidAnalysisStore.usersList,
  ]);

  // ============================================================
  // ATTRIBUTE HANDLERS
  // ============================================================

  const handleCreateAttribute = () => {
    setEditingAttribute(null);
    setAttributeForm({ attribute_name: '', attribute_description: '' });
    setAttributeModalVisible(true);
  };

  const handleEditAttribute = (attribute: Attribute) => {
    setEditingAttribute(attribute);
    setAttributeForm({
      attribute_name: attribute.attribute_name,
      attribute_description: attribute.attribute_description,
    });
    setAttributeModalVisible(true);
  };

  const handleDeleteAttribute = (attribute: Attribute) => {
    confirmDialog({
      message: `Are you sure you want to delete the attribute "${attribute.attribute_name}"?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          await BidAnalysisStore.deleteAttribute(attribute.id);
          toast.success('Attribute deleted successfully');

          // Refresh all tabs in the background - don't block the UI
          toast.info('Refreshing other tabs with updated data...', { autoClose: 2000 });
          Promise.all([
            BidAnalysisStore.fetchAttributes(true),
            BidAnalysisStore.fetchUserMappings(true),
            BidAnalysisStore.fetchAssignmentRules(true),
          ])
            .then(() => {
              toast.success('All tabs have been updated with the latest data.');
            })
            .catch(error => {
              console.error('Error refreshing tabs:', error);
              toast.warn('Attribute deleted, but some tabs may need manual refresh.');
            });
        } catch (error: any) {
          console.error('Error deleting attribute:', error);
          toast.error(error?.response?.data?.message || 'Failed to delete attribute');
        }
      },
    });
  };

  const handleSaveAttribute = async () => {
    if (!attributeForm.attribute_name.trim() || !attributeForm.attribute_description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSavingAttribute(true);
    try {
      if (editingAttribute) {
        // Only send fields that actually changed so the backend skips
        // the duplicate-name check when the name hasn't been modified.
        const payload: Record<string, string> = {};
        if (attributeForm.attribute_name !== editingAttribute.attribute_name) {
          payload.attribute_name = attributeForm.attribute_name;
        }
        if (attributeForm.attribute_description !== editingAttribute.attribute_description) {
          payload.attribute_description = attributeForm.attribute_description;
        }
        await BidAnalysisStore.updateAttribute(editingAttribute.id, payload);
        toast.success('Attribute updated successfully');
      } else {
        await BidAnalysisStore.createAttribute(attributeForm);
        toast.success('Attribute created successfully');
      }

      // Close modal immediately so user can continue working
      setAttributeModalVisible(false);
      setSavingAttribute(false);

      // Refresh all tabs in the background - don't block the UI
      toast.info('Refreshing other tabs with updated data...', { autoClose: 2000 });
      Promise.all([
        BidAnalysisStore.fetchAttributes(true),
        BidAnalysisStore.fetchUserMappings(true),
        BidAnalysisStore.fetchAssignmentRules(true),
      ])
        .then(() => {
          toast.success('All tabs have been updated with the latest data.');
        })
        .catch(error => {
          console.error('Error refreshing tabs:', error);
          toast.warn('Attribute saved, but some tabs may need manual refresh.');
        });
    } catch (error: any) {
      console.error('Error saving attribute:', error);
      toast.error(error?.response?.data?.message || 'Failed to save attribute');
      setSavingAttribute(false);
    }
  };

  // ============================================================
  // USER-ATTRIBUTE MAPPING HANDLERS
  // ============================================================

  const handleAddUsers = () => {
    setSelectedAttributeForAdd(null);
    setSelectedUserIds([]);
    setAddUsersModalVisible(true);
  };

  const handleEditUsers = async (attribute: AttributeWithUsers) => {
    setSelectedAttributeForUsers(attribute);
    if (BidAnalysisStore.usersList.length === 0) {
      await BidAnalysisStore.fetchUsersList();
    }

    const currentUserIds = attribute.users
      .map(attrUser => {
        const matchedUser = BidAnalysisStore.usersList.find(
          (user: any) => user.email?.toLowerCase() === attrUser.user_id?.toLowerCase()
        );
        return matchedUser ? matchedUser.email : attrUser.user_id;
      })
      .filter(Boolean);
    setSelectedUserIds(currentUserIds);
    setEditUsersModalVisible(true);
  };

  const handleSaveUsers = async () => {
    const attributeId = editUsersModalVisible
      ? selectedAttributeForUsers?.id
      : selectedAttributeForAdd?.id;

    if (!attributeId) {
      toast.error('Please select an attribute');
      return;
    }

    if (!editUsersModalVisible && selectedUserIds.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setSavingUsers(true);
    try {
      if (editUsersModalVisible && selectedAttributeForUsers) {
        const currentUserIds = selectedAttributeForUsers.users
          .map(attrUser => {
            const matchedUser = BidAnalysisStore.usersList.find(
              (user: any) => user.email?.toLowerCase() === attrUser.user_id?.toLowerCase()
            );
            return matchedUser ? matchedUser.email : attrUser.user_id;
          })
          .filter(Boolean);

        const selectedUserIdsLower = selectedUserIds.map(id => id?.toLowerCase());
        const currentUserIdsLower = currentUserIds.map(id => id?.toLowerCase());

        const usersToAdd = selectedUserIds.filter(
          id => !currentUserIdsLower.includes(id?.toLowerCase())
        );
        const usersToRemove = currentUserIds.filter(
          id => !selectedUserIdsLower.includes(id?.toLowerCase())
        );

        if (usersToAdd.length > 0) {
          await BidAnalysisStore.addUsersToAttribute(attributeId, { user_ids: usersToAdd });
        }
        if (usersToRemove.length > 0) {
          await BidAnalysisStore.removeUsersFromAttribute(attributeId, { user_ids: usersToRemove });
        }
        if (usersToAdd.length > 0 || usersToRemove.length > 0) {
          toast.success('Users updated successfully');
        }
      } else {
        await BidAnalysisStore.addUsersToAttribute(attributeId, { user_ids: selectedUserIds });
        toast.success('Users added successfully');
      }

      setAddUsersModalVisible(false);
      setEditUsersModalVisible(false);
      setSelectedAttributeForUsers(null);
      setSelectedAttributeForAdd(null);
      setSelectedUserIds([]);
    } catch (error: any) {
      console.error('Error updating users:', error);
      toast.error(error?.response?.data?.message || 'Failed to update users');
    } finally {
      setSavingUsers(false);
    }
  };

  // ============================================================
  // RULE HANDLERS
  // ============================================================

  const handleCreateRule = () => {
    setEditingRule(null);
    setRuleForm(createEmptyRuleForm());
    setRuleModalVisible(true);
  };

  const handleEditRule = (rule: AssignmentRule) => {
    setEditingRule(rule);
    setRuleForm(convertRuleToForm(rule));
    setRuleModalVisible(true);
  };

  const handleDeleteRule = (rule: AssignmentRule) => {
    confirmDialog({
      message: `Are you sure you want to delete the rule "${rule.rule_name}"?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          await BidAnalysisStore.deleteAssignmentRule(rule.id);
          toast.success('Rule deleted successfully');
        } catch (error: any) {
          console.error('Error deleting rule:', error);
          toast.error(error?.response?.data?.message || 'Failed to delete rule');
        }
      },
    });
  };

  const handleSaveRule = async () => {
    const validationError = validateRuleForm(ruleForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSavingRule(true);
    try {
      const payload = convertFormToPayload(ruleForm);
      if (editingRule) {
        await BidAnalysisStore.updateAssignmentRule(editingRule.id, payload);
        toast.success('Rule updated successfully');
      } else {
        await BidAnalysisStore.createAssignmentRule(payload);
        toast.success('Rule created successfully');
      }
      setRuleModalVisible(false);
      setSavingRule(false);
    } catch (error: any) {
      console.error('Error saving rule:', error);
      toast.error(error?.response?.data?.message || 'Failed to save rule');
      setSavingRule(false);
    }
  };

  // ============================================================
  // SUPPLIER DISCOUNT HANDLERS
  // ============================================================

  const handleCreateSupplierDiscount = () => {
    setEditingSupplierDiscount(null);
    setSupplierDiscountForm({
      supplier: '',
      supplier_name: '',
      supplier_code: '',
      prompt_pay_discount: null,
      admin_fee: null,
    });
    setSupplierDiscountModalVisible(true);
  };

  const handleEditSupplierDiscount = (discount: SupplierDiscount) => {
    setEditingSupplierDiscount(discount);
    setSupplierDiscountForm({
      supplier: discount.supplier,
      supplier_name: discount.supplier_name,
      supplier_code: discount.supplier_code,
      prompt_pay_discount: discount.prompt_pay_discount,
      admin_fee: discount.admin_fee,
    });
    setSupplierDiscountModalVisible(true);
  };

  const handleDeleteSupplierDiscount = (discount: SupplierDiscount) => {
    confirmDialog({
      message: `Are you sure you want to delete the supplier discount for "${discount.supplier_name}" (${discount.supplier_code})?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          await BidAnalysisStore.deleteSupplierDiscount(discount.id);
          toast.success('Supplier discount deleted successfully');
        } catch (error: any) {
          console.error('Error deleting supplier discount:', error);
          toast.error(error?.response?.data?.message || 'Failed to delete supplier discount');
        }
      },
    });
  };

  const handleSaveSupplierDiscount = async () => {
    if (
      !supplierDiscountForm.supplier.trim() ||
      !supplierDiscountForm.supplier_name.trim() ||
      !supplierDiscountForm.supplier_code.trim()
    ) {
      toast.error('Please fill in all required fields (Supplier, Supplier Name, Supplier Code)');
      return;
    }
    if (!/^\d{5}$/.test(supplierDiscountForm.supplier_code)) {
      toast.error('Supplier code must be exactly 5 numeric digits');
      return;
    }

    const existingDiscount = editingSupplierDiscount
      ? BidAnalysisStore.supplierDiscounts.find(
          d =>
            d.id !== editingSupplierDiscount.id &&
            d.supplier_code === supplierDiscountForm.supplier_code &&
            d.is_active !== false
        )
      : BidAnalysisStore.supplierDiscounts.find(
          d => d.supplier_code === supplierDiscountForm.supplier_code && d.is_active !== false
        );
    if (existingDiscount) {
      toast.error('A supplier discount with this supplier code already exists');
      return;
    }

    setSavingSupplierDiscount(true);
    try {
      if (editingSupplierDiscount) {
        await BidAnalysisStore.updateSupplierDiscount(
          editingSupplierDiscount.id,
          supplierDiscountForm
        );
        toast.success('Supplier discount updated successfully');
      } else {
        await BidAnalysisStore.createSupplierDiscount(supplierDiscountForm);
        toast.success('Supplier discount created successfully');
      }

      setSupplierDiscountModalVisible(false);
      setSavingSupplierDiscount(false);
    } catch (error: any) {
      console.error('Error saving supplier discount:', error);
      toast.error(error?.response?.data?.message || 'Failed to save supplier discount');
      setSavingSupplierDiscount(false);
    }
  };

  // ============================================================
  // SUPPLIER DISCOUNT FILTERING
  // ============================================================

  // Filter supplier discounts by supplier and supplier_name
  const filteredSupplierDiscounts = useMemo(() => {
    if (!supplierDiscountSearch.trim()) {
      return BidAnalysisStore.supplierDiscounts;
    }

    const searchLower = supplierDiscountSearch.toLowerCase().trim();
    return BidAnalysisStore.supplierDiscounts.filter(
      discount =>
        (discount.supplier?.toLowerCase().includes(searchLower) ||
          discount.supplier_name?.toLowerCase().includes(searchLower)) ??
        false
    );
  }, [BidAnalysisStore.supplierDiscounts, supplierDiscountSearch]);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className={classes.referenceTablesContainer}>
      <div className={classes.header}>
        <Button
          icon="pi pi-arrow-left"
          label="Back to Dashboard"
          className="p-button-text"
          onClick={() => navigate('/bdportal/bid-analysis/dashboard')}
        />
        <h1>Reference Tables</h1>
      </div>

      <TabView
        activeIndex={activeTab}
        onTabChange={e => setActiveTab(e.index)}
        className={classes.tabView}
      >
        {/* Attributes Tab */}
        <TabPanel header="Attributes">
          <div className={classes.tabContent}>
            <div className={classes.tableHeader}>
              <h2>Attributes</h2>
              <Button
                label="Create Attribute"
                icon="pi pi-plus"
                onClick={handleCreateAttribute}
                disabled={BidAnalysisStore.isLoadingAttributes}
                className={classes.primaryButton}
              />
            </div>

            {BidAnalysisStore.isLoadingAttributes ? (
              <div className={classes.loadingContainer}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '2rem', color: '#522d6e' }}
                ></i>
                <p>Loading attributes...</p>
              </div>
            ) : (
              <DataTable
                value={BidAnalysisStore.attributes}
                emptyMessage="No attributes found"
                className={classes.dataTable}
              >
                <Column
                  field="attribute_name"
                  header="Attribute Name"
                  body={attributeNameBodyTemplate}
                  sortable
                />
                <Column
                  field="attribute_description"
                  header="Attribute Description"
                  body={attributeDescriptionBodyTemplate}
                  sortable
                />
                <Column
                  header="Actions"
                  body={createAttributeActionsBodyTemplate(
                    handleEditAttribute,
                    handleDeleteAttribute
                  )}
                  style={{ width: '120px' }}
                />
              </DataTable>
            )}
          </div>
        </TabPanel>

        {/* User-Attribute Mapping Tab */}
        <TabPanel header="User-Attribute Mapping">
          <div className={classes.tabContent}>
            <div className={classes.tableHeader}>
              <h2>User-Attribute Mappings</h2>
              <Button
                label="Add Users"
                icon="pi pi-plus"
                onClick={handleAddUsers}
                disabled={BidAnalysisStore.isLoadingUserMappings}
                className={classes.primaryButton}
              />
            </div>

            {BidAnalysisStore.isLoadingUserMappings ? (
              <div className={classes.loadingContainer}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '2rem', color: '#522d6e' }}
                ></i>
                <p>Loading user-attribute mappings...</p>
              </div>
            ) : (
              <DataTable
                value={BidAnalysisStore.attributesWithUsers}
                emptyMessage="No user-attribute mappings found"
                className={classes.dataTable}
                rowClassName={() => classes.attributeRow}
              >
                <Column
                  field="attribute_name"
                  header="Attribute"
                  body={mappingAttributeNameBodyTemplate}
                  sortable
                  style={{ minWidth: '250px' }}
                />
                <Column
                  field="user_count"
                  header="User Count"
                  body={userCountBodyTemplate}
                  sortable
                  style={{ width: '120px' }}
                />
                <Column
                  header="Users"
                  body={usersListBodyTemplate}
                  style={{ minWidth: '300px', maxWidth: '500px' }}
                />
                <Column
                  header="Actions"
                  body={createMappingAttributeActionsBodyTemplate(handleEditUsers)}
                  style={{ width: '100px' }}
                />
              </DataTable>
            )}
          </div>
        </TabPanel>

        {/* Rule-Attribute Mapping Tab */}
        <TabPanel header="Rule-Attribute Mapping">
          <div className={classes.tabContent}>
            <div className={classes.tableHeader}>
              <h2>Assignment Rules</h2>
              <Button
                label="Create Rule"
                icon="pi pi-plus"
                onClick={handleCreateRule}
                disabled={BidAnalysisStore.isLoadingRules}
                className={classes.primaryButton}
              />
            </div>

            {BidAnalysisStore.isLoadingRules ? (
              <div className={classes.loadingContainer}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '2rem', color: '#522d6e' }}
                ></i>
                <p>Loading assignment rules...</p>
              </div>
            ) : (
              <DataTable
                value={BidAnalysisStore.assignmentRules}
                emptyMessage="No assignment rules found"
                className={classes.dataTable}
              >
                <Column
                  field="rule_name"
                  header="Rule Name"
                  body={ruleNameBodyTemplate}
                  sortable
                  style={{ minWidth: '200px' }}
                />
                <Column
                  field="rule_json.contract_type"
                  header="Contract Type"
                  body={contractTypeBodyTemplate}
                  sortable
                  style={{ width: '150px' }}
                />
                <Column
                  header="Conditions"
                  body={conditionsBodyTemplate}
                  style={{ minWidth: '250px' }}
                />
                <Column
                  header="Mapped Attributes"
                  body={ruleAttributesBodyTemplate}
                  style={{ minWidth: '200px' }}
                />
                <Column
                  header="Actions"
                  body={createRuleActionsBodyTemplate(handleEditRule, handleDeleteRule)}
                  style={{ width: '120px' }}
                />
              </DataTable>
            )}
          </div>
        </TabPanel>

        {/* Supplier Discount Tab */}
        <TabPanel header="Supplier Discount">
          <div className={classes.tabContent}>
            <div className={classes.tableHeader}>
              <h2>Supplier Discount</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div
                  className={classes.searchInputWrapper}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                      value={supplierDiscountSearch}
                      onChange={e => setSupplierDiscountSearch(e.target.value)}
                      placeholder="Search by supplier or supplier name..."
                      style={{ width: '400px' }}
                    />
                  </span>
                </div>
                <Button
                  label="Create Supplier Discount"
                  icon="pi pi-plus"
                  onClick={handleCreateSupplierDiscount}
                  disabled={BidAnalysisStore.isLoadingSupplierDiscounts}
                  className={classes.primaryButton}
                />
              </div>
            </div>

            {BidAnalysisStore.isLoadingSupplierDiscounts ? (
              <div className={classes.loadingContainer}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '2rem', color: '#522d6e' }}
                ></i>
                <p>Loading supplier discounts...</p>
              </div>
            ) : (
              <DataTable
                value={filteredSupplierDiscounts}
                emptyMessage="No supplier discounts found"
                className={classes.dataTable}
              >
                <Column
                  field="supplier"
                  header="Supplier"
                  body={supplierBodyTemplate}
                  sortable
                  style={{ minWidth: '150px' }}
                />
                <Column
                  field="supplier_name"
                  header="Supplier Name"
                  body={supplierNameBodyTemplate}
                  sortable
                  style={{ minWidth: '200px' }}
                />
                <Column
                  field="supplier_code"
                  header="Supplier Code"
                  body={supplierCodeBodyTemplate}
                  sortable
                  style={{ width: '140px' }}
                />
                <Column
                  field="prompt_pay_discount"
                  header="Prompt Pay Discount (%)"
                  body={promptPayDiscountBodyTemplate}
                  sortable
                  style={{ width: '180px' }}
                />
                <Column
                  field="admin_fee"
                  header="Admin Fee (%)"
                  body={adminFeeBodyTemplate}
                  sortable
                  style={{ width: '140px' }}
                />
                <Column
                  header="Actions"
                  body={createSupplierDiscountActionsBodyTemplate(
                    handleEditSupplierDiscount,
                    handleDeleteSupplierDiscount
                  )}
                  style={{ width: '120px' }}
                />
              </DataTable>
            )}
          </div>
        </TabPanel>
      </TabView>

      {/* Modals */}
      <AttributeModal
        visible={attributeModalVisible}
        onHide={() => {
          setAttributeModalVisible(false);
          setSavingAttribute(false);
        }}
        onSave={handleSaveAttribute}
        editingAttribute={editingAttribute}
        attributeForm={attributeForm}
        onFormChange={setAttributeForm}
        saving={savingAttribute}
      />

      <UserAttributeModal
        addModalVisible={addUsersModalVisible}
        onAddModalHide={() => {
          setAddUsersModalVisible(false);
          setSelectedAttributeForAdd(null);
          setSelectedUserIds([]);
        }}
        onSaveUsers={handleSaveUsers}
        selectedAttributeForAdd={selectedAttributeForAdd}
        onSelectAttributeForAdd={setSelectedAttributeForAdd}
        editModalVisible={editUsersModalVisible}
        onEditModalHide={() => {
          setEditUsersModalVisible(false);
          setSelectedAttributeForUsers(null);
          setSelectedUserIds([]);
        }}
        selectedAttributeForUsers={selectedAttributeForUsers}
        selectedUserIds={selectedUserIds}
        onSelectUserIds={setSelectedUserIds}
        attributes={BidAnalysisStore.attributes}
        usersList={BidAnalysisStore.usersList}
        isLoadingUsersList={BidAnalysisStore.isLoadingUsersList}
        savingUsers={savingUsers}
      />

      <RuleModal
        visible={ruleModalVisible}
        onHide={() => setRuleModalVisible(false)}
        onSave={handleSaveRule}
        editingRule={editingRule}
        ruleForm={ruleForm}
        onFormChange={setRuleForm}
        attributes={BidAnalysisStore.attributes}
        savingRule={savingRule}
      />

      <SupplierDiscountModal
        visible={supplierDiscountModalVisible}
        onHide={() => {
          setSupplierDiscountModalVisible(false);
          setSavingSupplierDiscount(false);
        }}
        onSave={handleSaveSupplierDiscount}
        editingDiscount={editingSupplierDiscount}
        discountForm={supplierDiscountForm}
        onFormChange={setSupplierDiscountForm}
        saving={savingSupplierDiscount}
      />

      <ConfirmDialog />
    </div>
  );
});

export default ReferenceTables;
